import { ItemType } from '@/components/item/itemType';
import { useCollapsibleState } from '@/components/table/collapsibleCell';
import { CollapsibleRow } from '@/components/table/collapsibleRow';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Text } from '@/components/ui/typography';

import { useSeasonPass } from '../api/getSeasonPass';
import type { SeasonPassListItem } from '../models/seasonPass';

// The list endpoint carries no rewards: only the detail does, so a pass reads
// its own once opened.
const SeasonPassRewards = ({ id }: { id: string }) => {
  const { data: pass, isPending, isError } = useSeasonPass(id);

  if (isPending) {
    return (
      <div aria-busy className="flex flex-col gap-2.5 rounded-md bg-card p-2.5">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3.5 w-1/3" />
      </div>
    );
  }
  if (isError) {
    return <Text variant="muted">Could not load the rewards.</Text>;
  }
  if (pass.rewards.length === 0) {
    return <Text variant="muted">No reward.</Text>;
  }

  const rewards = [...pass.rewards].sort((a, b) => a.level - b.level);

  return (
    <div className="max-h-60 overflow-y-auto rounded-md bg-card">
      <Table noWrapper stickyHeader className="[&_thead_th]:bg-card">
        <TableHeader className="bg-transparent">
          <TableRow>
            <TableHead className="w-16 text-right">Level</TableHead>
            <TableHead>Reward</TableHead>
            <TableHead className="w-16 text-right">Qty</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rewards.map(({ level, item, quantity }, index) => (
            <TableRow
              // Static once sorted, and a level can repeat an item.
              key={`${level}-${index.toString()}`}
              className="hover:bg-transparent"
            >
              <TableCell className="py-1.5 text-right tabular-nums text-muted-foreground">
                {level}
              </TableCell>
              <TableCell className="py-1.5">
                {item ? (
                  <div className="flex items-center gap-2">
                    <ItemType
                      type={item.type}
                      item_rarity={item.item_rarity}
                      size={16}
                    />
                    <span className="font-mono">{item.code}</span>
                  </div>
                ) : (
                  // A soft-deleted reward item is left out by the API.
                  <Text variant="caption">Deleted item</Text>
                )}
              </TableCell>
              <TableCell className="py-1.5 text-right tabular-nums">
                ×{quantity}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/** Season passes by code and max level, each opening on its rewards. */
export const SeasonPassesTable = ({
  seasonPasses,
}: {
  seasonPasses: SeasonPassListItem[];
}) => {
  const collapsible = useCollapsibleState(
    seasonPasses.map(({ season_pass_id }) => season_pass_id),
  );

  return (
    <Table noWrapper>
      <TableHeader className="bg-transparent">
        <TableRow>
          <TableHead className="w-8">
            <span className="sr-only">Expand</span>
          </TableHead>
          <TableHead>Code</TableHead>
          <TableHead className="w-24 text-right">Max level</TableHead>
        </TableRow>
      </TableHeader>
      {seasonPasses.map((pass) => (
        <CollapsibleRow
          key={pass.season_pass_id}
          open={collapsible.isExpanded(pass.season_pass_id)}
          onToggle={() => {
            collapsible.toggle(pass.season_pass_id);
          }}
          expanded={<SeasonPassRewards id={pass.season_pass_id} />}
          colSpan={3}
        >
          <TableCell>
            <div className="font-mono">{pass.code}</div>
            <Text variant="caption">{pass.name}</Text>
          </TableCell>
          <TableCell className="text-right tabular-nums">
            {pass.max_level ?? '—'}
          </TableCell>
        </CollapsibleRow>
      ))}
    </Table>
  );
};
